import type { TestResults } from '../types/HearingTestTypes';
import type { HearingTestFields, ResultTile } from '../types/SitecoreFields';

function getSeverityLevel(
  averageScore: number,
  goodMax: number,
  moderateMax: number
): 'good' | 'moderate' | 'severe' {
  if (averageScore <= goodMax) {
    return 'good';
  }
  if (averageScore <= moderateMax) {
    return 'moderate';
  }
  return 'severe';
}

function calculateAverage(scores: number[]): number {
  if (scores.length === 0) return 0;
  const sum = scores.reduce((acc, score) => acc + score, 0);
  return sum / scores.length;
}

function filterProductsBySeverity(
  tiles: ResultTile[],
  userSeverity: 'good' | 'moderate' | 'severe'
): ResultTile[] {
  return tiles.filter((tile) => {
    const productSeverities = tile.severityLevel.map((item) => item.Value.value);
    return productSeverities.includes(userSeverity);
  });
}

const LOWEST_PRIORITY = 999;

function sortByPriority(tiles: ResultTile[]): ResultTile[] {
  return [...tiles].sort((a, b) => {
    const priorityA = a.displayPriority.value ?? LOWEST_PRIORITY;
    const priorityB = b.displayPriority.value ?? LOWEST_PRIORITY;
    return priorityA - priorityB;
  });
}

export function calculateTestResults(
  leftEarScores: number[],
  rightEarScores: number[],
  sitecoreFields: HearingTestFields
): TestResults {
  const leftEarAverage = calculateAverage(leftEarScores);
  const rightEarAverage = calculateAverage(rightEarScores);

  const goodMax = sitecoreFields.goodHearingMaxScore.value;
  const moderateMax = sitecoreFields.moderateLossMaxScore.value;

  const leftEarSeverity = getSeverityLevel(leftEarAverage, goodMax, moderateMax);
  const rightEarSeverity = getSeverityLevel(rightEarAverage, goodMax, moderateMax);

  const overallSeverityLevel: 'good' | 'moderate' | 'severe' =
    leftEarSeverity === 'severe' || rightEarSeverity === 'severe'
      ? 'severe'
      : leftEarSeverity === 'moderate' || rightEarSeverity === 'moderate'
        ? 'moderate'
        : 'good';

  return {
    leftEarAverage,
    rightEarAverage,
    overallSeverityLevel,
    leftEarSeverity,
    rightEarSeverity,
  };
}

export function getRecommendedProducts(
  sitecoreFields: HearingTestFields,
  overallSeverityLevel: 'good' | 'moderate' | 'severe'
): ResultTile[] {
  const matchingProducts = filterProductsBySeverity(
    sitecoreFields.productTiles,
    overallSeverityLevel
  );

  const sortedProducts = sortByPriority(matchingProducts);
  return sortedProducts.slice(0, 3);
}
